import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { buildHttpParams } from '@orderna/admin-panel/src/utils/build-http-params';
import { SuccessResponse } from '../shared/success-response';
import { CreateLiveOrderRequestDto } from './dtos/create-live-order.request.dto';
import { CreateLiveOrderCommentRequestDto } from './dtos/create-live-order-comment.request.dto';
import { FindLiveOrderResponseDto } from './dtos/find-live-order.response.dto';
import { FindLiveOrdersQueryDto } from './dtos/find-live-orders.query.dto';
import { FindLiveOrdersResponseDto } from './dtos/find-live-orders.response.dto';
import { UpdateLiveOrderStatusRequestDto } from './dtos/update-live-order-status.request.dto';
import { SendLiveOrderCancellationRequestDto } from './dtos/send-live-order-cancellation.request.dto';
import { UpdateLiveOrderStatusResponseDto } from './dtos/update-live-order-status.response.dto';
import { CreateLiveOrderCommentResponseDto } from './dtos/create-live-order-comment.response.dto';
import { SendLiveOrderCancellationResponseDto } from './dtos/send-live-order-cancellation.response.dto';

@Injectable({
  providedIn: 'root',
})
export class LiveOrdersApiService {
  private readonly http = inject(HttpClient);
  private readonly API_URL = `${environment.api}/live-orders`;

  async findLiveOrders(
    query: FindLiveOrdersQueryDto = {},
  ): Promise<FindLiveOrdersResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindLiveOrdersResponseDto>>(this.API_URL, {
        params: buildHttpParams(query),
      }),
    );
    return response.data;
  }

  async findLiveOrder(id: number): Promise<FindLiveOrderResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindLiveOrderResponseDto>>(
        `${this.API_URL}/${id}`,
      ),
    );
    return response.data;
  }

  async createLiveOrder(dto: CreateLiveOrderRequestDto): Promise<number> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<number>>(this.API_URL, dto),
    );
    return response.data;
  }

  async updateLiveOrderStatus(
    id: number,
    dto: UpdateLiveOrderStatusRequestDto,
  ): Promise<UpdateLiveOrderStatusResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateLiveOrderStatusResponseDto>>(
        `${this.API_URL}/${id}/status`,
        dto,
      ),
    );
    return response.data;
  }

  async createLiveOrderComment(
    id: number,
    dto: CreateLiveOrderCommentRequestDto,
  ): Promise<CreateLiveOrderCommentResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateLiveOrderCommentResponseDto>>(
        `${this.API_URL}/${id}/comments`,
        dto,
      ),
    );

    return response.data;
  }

  async sendLiveOrderCancellationRequest(
    id: number,
    dto: SendLiveOrderCancellationRequestDto,
  ): Promise<SendLiveOrderCancellationResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<SendLiveOrderCancellationResponseDto>>(
        `${this.API_URL}/${id}/cancellation-requests`,
        dto,
      ),
    );

    return response.data;
  }
}
